import { Component, inject, signal } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { FormsModule, NgForm } from '@angular/forms';
import { LoadingIndicator } from '../../../shared/loading-indicator/loading-indicator';
import { IAcademicYears } from '../../../interfaces/academic-year';
import { ApiEndpoints } from '../../../core/api-endpoints';
import { AcademicYearService } from '../../../services/academic-year';
import { FlashService } from '../../../services/flash';
import { AcademicTermService } from '../../../services/academic-term';
import { IAcademicTerm, IAcademicTerms } from '../../../interfaces/academic-term';
import { LevelService } from '../../../services/level';
import { ILevels } from '../../../interfaces/level';
import { Helpers } from '../../../utilities/helpers';
import { FeeService } from '../../../services/fees';
import { StudentService } from '../../../services/student';
import { IStudent, IStudents } from '../../../interfaces/student';
import { IPayments, IStudentFees, IFeeStructures } from '../../../interfaces/fees';
import { DashboardService } from '../../../services/dashboard';
import { DecodedToken, Session } from '../../../services/session';
import { ShimmerEffect } from '../../../shared/shimmer-effect/shimmer-effect';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-student-fees',
  imports: [
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    FormsModule,
    LoadingIndicator,
    ShimmerEffect,
  ],
  templateUrl: './student-fees.html',
  styleUrl: './student-fees.css',
})
export class StudentFees {
  constructor(
    private academicYearService: AcademicYearService,
    protected academicTermService: AcademicTermService,
    private levelService: LevelService,
    private feeService: FeeService,
    private router: Router,
    private studentService: StudentService,
    private dashboardService: DashboardService,
    private session: Session,
  ) {
    this.getAcademicYears();
    this.getLevels();

    const token = this.session.getItem('token');

    this.loggedUserRole = this.session.decodeToken(token)?.role;

    if (this.loggedUserRole !== 'SUPER_ADMIN') {
      this.getFeeCards();
    }
  }

  protected loggedUserRole?: string;
  protected setFee = signal<boolean>(false);
  protected isEditing = signal<boolean>(false);
  protected academicYears = signal<IAcademicYears | null>(null);
  protected academicYearId: string = '';
  protected readonly flashService = inject(FlashService);
  protected academicTerms = signal<IAcademicTerms | null>(null);
  protected academicTermId: string = '';
  protected levels = signal<ILevels | null>(null);
  protected feeStructureStatuses = ['DRAFT', 'ACTIVE'];
  protected feeName!: string;
  protected feeType!: string;
  protected feeAmount!: string;
  protected feeDueDate!: string;
  protected feeRequired: boolean = true;
  protected feeStatus: string = 'ACTIVE';
  protected readonly Helpers = Helpers;
  protected setNewPayment = signal<boolean>(false);
  protected studentFeeId: string = '';
  protected paymentAmount: string = '';
  protected paymentDate: string = '';
  protected paymentMethod: string = '';
  protected transactionReference!: string;
  protected additionalNotes: string = '';
  protected query: string = '';
  protected students = signal<IStudents | null>(null);
  protected selectedStudent!: IStudent['payload'];
  protected fees = signal<IStudentFees | null>(null);
  protected readonly paymentMethods = ['CASH', 'MOBILE_MONEY', 'BANK_TRANSFER', 'CARD', 'CHEQUE'];
  protected selectedLevels: string[] = [];
  protected studentFeePayments = signal<IPayments | null>(null);
  protected feeStructureFallback = signal<IFeeStructures | null>(null);
  protected feeCards = signal<any>(null);
  protected isLoading = signal<boolean>(false);
  protected selectedAcademicTerm!: IAcademicTerms['payload'][number] | undefined;

  protected selectLevel(levelId: string): void {
    if (this.selectedLevels.includes(levelId)) {
      this.selectedLevels = this.selectedLevels.filter((id) => id !== levelId);

      return;
    }

    this.selectedLevels = [...this.selectedLevels, levelId];
  }

  protected get areAllLevelsSelected(): boolean {
    const allLevels = this.levels()?.payload ?? [];
    return (
      allLevels.length > 0 && allLevels.every((level) => this.selectedLevels.includes(level.id))
    );
  }

  protected selectAllLevels(): void {
    const allLevels = this.levels()?.payload ?? [];

    if (this.areAllLevelsSelected) {
      this.selectedLevels = [];
    } else {
      this.selectedLevels = allLevels.map((level) => level.id);
    }
  }

  protected selectAcademicTerm(id: string): void {
    this.selectedAcademicTerm = this.academicTerms()?.payload?.find((term) => term.id === id);
  }

  protected getStudentPayments(studentFeeId: string): void {
    if (studentFeeId === '') {
      return;
    }

    this.feeStructureFallback.set(null);

    const params: Record<string, string | number> = {
      page: 0,
      size: 100,
      student_fee_id: studentFeeId,
    };

    this.feeService.getPayments(`${ApiEndpoints.url}/payment`, params).subscribe({
      next: (res) => {
        this.studentFeePayments.set(res);

        if (!res.payload.length) {
          this.getFeeStructure(studentFeeId);
        }
      },
      error: (err) => {
        this.flashService.failure(
          err?.error?.message ?? 'Could not load payments. Refresh the page',
        );
      },
    });
  }

  protected getFeeStructure(studentFeeId: string): void {
    const studentFee = this.fees()?.payload?.find((f) => f.id === studentFeeId && f.status === 'ACTIVE');

    if (!studentFee?.fee_structure_id) {
      return;
    }

    const params: Record<string, string | number> = {
      page: 0,
      size: 1,
      fee_structure_id: studentFee.fee_structure_id,
    };

    this.feeService
      .getFeeStructures(`${ApiEndpoints.url}/fee-structure`, params)
      .subscribe({
        next: (res) => {
          this.feeStructureFallback.set(res);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load fee. Refresh the page',
          );
        },
      });
  }

  protected get paymentRemainingBalance(): number | null {
    const payments = this.studentFeePayments()?.payload;

    if (payments?.length) {
      return payments[0].student_fee_remaining_balance;
    }

    const fallback = this.feeStructureFallback()?.payload?.[0] as
      | (IFeeStructures['payload'][number] & { final_amount?: string })
      | undefined;

    if (fallback) {
      return Number(fallback.final_amount ?? fallback.amount);
    }

    return null;
  }

  protected get selectedStudentFeeFinalAmount(): number | null {
    const fee = this.fees()?.payload?.find((f) => f.id === this.studentFeeId);

    return fee ? Number(fee.final_amount) : null;
  }

  protected get isPaymentOverLimit(): boolean {
    const amount = Number(this.paymentAmount);

    if (!amount) {
      return false;
    }

    const remaining = this.paymentRemainingBalance;
    const finalAmount = this.selectedStudentFeeFinalAmount;

    const overRemaining = remaining !== null && amount > remaining;
    const overFinal = finalAmount !== null && amount > finalAmount;

    return overRemaining && overFinal;
  }

  protected getAcademicTerms(id: string): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 30,
      academic_year_id: id,
    };

    this.academicTermService
      .getAcademicTerms(`${ApiEndpoints.url}/academic-term`, params)
      .subscribe({
        next: (res) => {
          this.academicTerms.set(res);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load Academic Terms. Refresh the page.',
          );
        },
      });
  }

  protected selectStudent(student: IStudent['payload']) {
    this.getFees(student.id);
    this.query = '';
    this.selectedStudent = student;
  }

  protected getLevels(): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 50,
    };

    this.levelService.getLevels(`${ApiEndpoints.url}/level`, params).subscribe({
      next: (res) => {
        this.levels.set(res);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Could not load levels. Refresh the page');
      },
    });
  }

  protected getAcademicYears(): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 30,
    };

    this.academicYearService
      .getAcademicYears(`${ApiEndpoints.url}/academic-year`, params)
      .subscribe({
        next: (res) => {
          this.academicYears.set(res);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load Academic Years. Refresh the page.',
          );
        },
      });
  }

  protected addFee(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    this.isEditing.set(true);

    const newFeeForm = {
      level_ids: this.selectedLevels,
      term_id: this.academicTermId,
      academic_year_id: this.academicYearId,
      fee_name: this.feeName,
      fee_type: this.feeType,
      amount: this.feeAmount,
      due_date: this.feeDueDate,
      status: this.feeStatus,
      required: this.feeRequired,
    };

    this.feeService.create(`${ApiEndpoints.url}/fee-structure`, newFeeForm).subscribe({
      next: () => {
        this.flashService.success('Fee created successfully.');
        this.isEditing.set(false);
        this.setFee.set(false);
        window.location.reload();
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Error creating fee. Try again.');
        this.isEditing.set(false);
      },
    });
  }

  protected getStudents(value: string): void {
    this.studentFeeId = '';
    this.fees.set(null);

    const params = {
      page: 0,
      size: 50,
      status: 'ACTIVE',
    };

    this.studentService
      .getStudents(`${ApiEndpoints.url}/student?search=${value}`, params)
      .subscribe({
        next: (data) => {
          this.students.set(data);
        },
        error: (err) => {
          this.flashService.failure(err?.error?.message ?? 'Error loading students.');
        },
      });
  }

  protected getFees(id: string): void {
    const params: Record<string, string | number> = {
      student_id: id,
    };

    this.feeService.getStudentFees(`${ApiEndpoints.url}/student-fee`, params).subscribe({
      next: (res) => {
        const studentFees = res.payload;

        const feeStructureIds = Array.from(
          new Set(studentFees.map((f) => f.fee_structure_id).filter(Boolean)),
        );

        if (!feeStructureIds.length) {
          this.fees.set(res);

          return;
        }

        const requests = feeStructureIds.map((feeStructureId) =>
          this.feeService.getFeeStructures(`${ApiEndpoints.url}/fee-structure`, {
            page: 0,
            size: 1,
            fee_structure_id: feeStructureId,
          }),
        );

        forkJoin(requests).subscribe({
          next: (results) => {
            const activeFeeStructureIds = new Set(
              feeStructureIds.filter(
                (feeStructureId, index) =>
                  results[index].payload?.[0]?.status === 'ACTIVE',
              ),
            );

            this.fees.set({
              ...res,
              payload: studentFees.filter((studentFee) =>
                activeFeeStructureIds.has(studentFee.fee_structure_id),
              ),
            });
          },
          error: () => {
            this.fees.set(res);
          },
        });
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Could not load fees. Refresh the page');
      },
    });
  }

  protected addPayment(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    if (!this.studentFeeId) {
      this.flashService.failure('Select a fee');
      return;
    }

    if (!this.selectedStudent.id || this.selectedStudent.id === '') {
      this.flashService.failure('Select a fee');
      return;
    }

    this.isEditing.set(true);

    const paymentForm = {
      student_id: this.selectedStudent.id,
      student_fee_id: this.studentFeeId,
      amount: this.paymentAmount,
      payment_date: this.paymentDate,
      payment_method: this.paymentMethod,
      transaction_reference: this.transactionReference,
      notes: this.additionalNotes,
    };

    this.feeService.create(`${ApiEndpoints.url}/payment`, paymentForm).subscribe({
      next: () => {
        this.flashService.success('Payment recorded successfully.');
        this.isEditing.set(false);
        this.setNewPayment.set(false);
        window.location.reload();
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Error recording payment. Try again.');
        this.isEditing.set(false);
      },
    });
  }

  protected isCurrentRoute(route: string): boolean {
    return this.router.url === route;
  }

  protected getFeeCards(): void {
    this.isLoading.set(true);

    this.dashboardService.getDashboardData(`${ApiEndpoints.url}/dashboard/fee-cards`).subscribe({
      next: (res) => {
        this.feeCards.set(res.payload);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.flashService.failure(
          err?.error?.message ?? 'Failed to load dashboard. Refresh the page.',
        );
      },
    });
  }
}
